export const DRIVER_AGENT_THREAD = "slick:agent"

/**
 * The dispatcher↔Slick AI conversation, stamped on both the question and the
 * answer. `slick:agent` alone is not enough: the backend puts that thread on
 * every dispatcher-only note in a driver room (fuel alerts, scan-check,
 * delays), which the whole office is meant to read. This stamp marks the half
 * that lives in the Slick AI column and nowhere else. The conversation is
 * shared — every colleague who has the column sees the same one — but a
 * colleague who never got the workspace has nowhere to read it, and without
 * this stamp the Q&A lands in their driver room as an internal note.
 */
export const isAgentChatMessage = (message: { agentChat?: boolean }): boolean =>
  message.agentChat === true

export const withoutAgentChat = <T extends { agentChat?: boolean }>(
  messages: T[],
): T[] => messages.filter((message) => !isAgentChatMessage(message))

/**
 * Progress the agent reports while it works ("Myślę…", the pipeline steps)
 * rides on chat-state stanzas carrying the same stamp as the conversation.
 * It belongs in the Slick AI column; a reader without the column has nowhere
 * to put it, and unapplied it would surface in their driver room as a Slick AI
 * bubble — and scroll the room to it — for the length of the turn. Unstamped
 * chat-state is Slick AI talking to the room the old way and stays.
 */
export const shouldApplyAgentChatState = (
  message: { agentChat?: boolean },
  hasAgentColumn: boolean,
): boolean => hasAgentColumn || !isAgentChatMessage(message)

const isDriverAgentThread = (message: { thread?: string }): boolean =>
  message.thread === DRIVER_AGENT_THREAD

const withoutDriverAgentThread = <T extends { thread?: string }>(
  messages: T[],
): T[] => messages.filter((message) => !isDriverAgentThread(message))

/**
 * Everything the driver got, whoever wrote it. The split is about who reads a
 * message, not who sent it: when the dispatcher tells the agent to ask the
 * driver something, the driver receives that message like any other, so it
 * belongs next to the driver's answer. What stays out is what the driver never
 * saw — the dispatcher↔agent thread and dispatcher-only notes, both stamped at
 * send time.
 */
const isDriverConversationMessage = (message: {
  thread?: string
  internal?: boolean
  agentChat?: boolean
}): boolean =>
  !isDriverAgentThread(message) &&
  !isAgentChatMessage(message) &&
  !message.internal

export const onlyDriverConversation = <
  T extends { thread?: string; internal?: boolean; agentChat?: boolean },
>(
  messages: T[],
): T[] => messages.filter(isDriverConversationMessage)

/** Slick AI column: the dispatcher↔agent Q&A plus dispatcher-only notes. */
const onlyAgentColumn = <
  T extends { thread?: string; internal?: boolean; agentChat?: boolean },
>(
  messages: T[],
): T[] => messages.filter((message) => !isDriverConversationMessage(message))

/**
 * Left pane of a driver room. Split on: the Slick AI column. Split off: the
 * whole room minus the agent conversation — the notes stamped slick:agent stay
 * (dropping the thread wholesale would hide fuel alerts on accounts that never
 * got the workspace), the dispatcher↔agent Q&A goes, because it lives in the
 * agent column and nowhere else.
 */
export const messagesForSlickAiPane = <
  T extends { thread?: string; internal?: boolean; agentChat?: boolean },
>(
  messages: T[],
  splitDriverChat: boolean,
): T[] =>
  splitDriverChat ? onlyAgentColumn(messages) : withoutAgentChat(messages)

/**
 * Chat list: what the preview quotes and what the row order follows. The agent
 * conversation is out either way — a driver room must not jump to the top, or
 * quote a line, because someone asked Slick AI a question in the column.
 * With the workspace on, the room's own notes leave the preview too: they have
 * their own place in the column.
 */
export const messagesForChatList = <
  T extends { thread?: string; agentChat?: boolean },
>(
  messages: T[],
  hideDriverAgentThread: boolean,
): T[] => {
  const withoutConversation = withoutAgentChat(messages)
  return hideDriverAgentThread
    ? withoutDriverAgentThread(withoutConversation)
    : withoutConversation
}
